import { useEffect, useMemo, useState } from 'react';

import { cn } from '@app/libs/utils';

import ArrowDownIcon from '../icons/ArrowDownIcon';

import { Button } from './Button';
import Dropdown from './Dropdown';

interface PaginateProps {
  total: number;
  page: number;
  pageSize: number;
  onChange?: (page: number, pageSize: number) => void;
  digits?: number;
  className?: string;
  sizes?: number[];
}

export const Paginate: React.FC<PaginateProps> = ({
  total,
  page: initalPage,
  pageSize = 12,
  onChange,
  digits = 3,
  className,
  sizes = [12, 24, 48, 96, 192],
}) => {
  const [start, setStart] = useState<number>(1);
  const [end, setEnd] = useState<number>(1);
  const [page, setPage] = useState<number>(initalPage);
  const totalPages = useMemo(() => Math.ceil(total / pageSize), [total, pageSize]);

  useEffect(() => {
    setPage(initalPage);
  }, [initalPage]);

  useEffect(() => {
    let start = Math.max(1, page - Math.floor(digits / 2));
    const end = Math.min(totalPages, start + digits - 1);
    start = Math.max(1, end - digits + 1);
    setStart(start);
    setEnd(end);
  }, [page, pageSize, total]);

  const handleChange = (page: number) => {
    page = Math.max(1, Math.min(page, totalPages));
    onChange?.(page, pageSize);
    setPage(page);
  };

  return (
    <div className="flex items-center gap-2 w-full justify-between flex-wrap">
      <span className="text-sm text-gray-500">
        Hiển thị từ {pageSize * (page - 1) + 1} đến {Math.min(pageSize * page, total)} bản ghi{' '}
      </span>
      <div className="flex items-center gap-5 text-sm justify-between flex-wrap w-full lg:w-auto">
        <Dropdown
          options={sizes.map(size => ({
            value: size.toString(),
            label: size.toString(),
          }))}
          triggerClassName="gap-3 items-center gap-2 border border-gray-300 rounded-lg px-3 py-1 h-10"
          value={pageSize.toString()}
          onChange={value => onChange?.(1, parseInt(value as string))}
        >
          <button className="text-sm flex">{pageSize} bản ghi</button>
          <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
        </Dropdown>
        {totalPages > 1 && (
          <ul className={cn('flex items-center gap-0.5 text-base', className)}>
            <li
              className="cursor-pointer rounded-lg px-2 py-1 w-10 h-10 flex items-center justify-center"
              onClick={() => handleChange(1)}
            >
              <ArrowDownIcon className={cn('w-4 h-4 rotate-90', page === 1 && 'text-gray-200')} />
            </li>
            {Array.from({ length: end - start + 1 }, (_, index) => (
              <li
                key={index}
                onClick={() => onChange?.(start + index, pageSize)}
                className={cn(
                  'cursor-pointer rounded-lg px-2 py-1 min-w-10 h-10 flex items-center justify-center px-2',
                  'hover:bg-blue-500 hover:text-white transition-all',
                  page === start + index && 'bg-blue-800 text-white'
                )}
              >
                {start + index}
              </li>
            ))}
            <li
              className="cursor-pointer rounded-lg px-2 py-1 w-10 h-10 flex items-center justify-center"
              onClick={() => handleChange(totalPages)}
            >
              <ArrowDownIcon
                className={cn('w-4 h-4 rotate-270', page === totalPages && 'text-gray-200')}
              />
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};
